import { Promotion } from '.';

export enum OrderType {
  Regular = 1,
  Group = 2,
}

export enum OrderStatus {
  InProcess = 'INPROCESS',
  Submitted = 'SUBMITTED',
}

export enum OrderErrorCode {
  BadLocation = 'COP-1004',
  OrderCompleted = 'COP-1008',
  MenuNotAvailableForLocation = 'COP-1020',
  EntreeNotAvailable = 'COP-1021',
  EntreeContentNotAvailable = 'COP-1022',
  SideNotAvailable = 'COP-1023',
  DrinkNotAvailable = 'COP-1024',
  PickupDateTimeRequired = 'COP-1062',
  PaymentRequired = 'COP-1063',
  PaymentTypeInvalid = 'COP-1064',
  CardholderNameRequired = 'COP-1066',
  OrderNotSubmitted = 'COP-1068',
  CardExpiryRequired = 'COP-1069',
  PaymentRequiredWhenDelivery = 'COP-1079',
  PaymentRequiredWhenPromotions = 'COP-1080',
  OnlineOrderingDisabledForLocation = 'COP-1081',
  PaymentNotAllowedForOutsideUS = 'COP-1082',
  UnableToCalculateDeliveryFee = 'COP-1084',
  UnableToCalculatePromoDiscount = 'COP-1085',
  UnableToReservePickupTime = 'COP-1086',
  OrderDoesNotMeetMinDeliveryValue = 'COP-1087',
  // Technically the backend returns "Unable to validate the delivery information", but we can infer it's only the phone
  InvalidPhoneNumber = 'COP-1089',
  PurchaseNotCompleted = 'COP-1090',
  PurchaseNotApproved = 'COP-1091',
  UnableToValidateGiftCardBalance = 'COP-1093',
  InsufficientGiftCardBalance = 'COP-1094',
  DeliveryFailure = 'COP-1095',
  MaxGroupParticipantsExceeded = 'COP-1108',
  RewardExceedsLimit = 'COP-1110',
  OrderNotSubmittedButPaymentCollected = 'COP-1112',
  NonFoodItemNotAvailable = 'COP-1127',
  DeliveryTipMaxExceeded = 'COP-1131',
  PaymentProviderMismatch = 'COP-1133',
  DonationCampaignExpired = 'COP-1135',
  PromotionCodeInvalidLocation = 'COP-1136',
  PromotionCodeAlreadyRedeemed = 'COP-1137',
  PromotionCodeHasExpired = 'COP-1138',
  PromotionCodeInvalidAvailability = 'COP-1139',
  PromotionCodeInvalidMinimumAmount = 'COP-1140',
  PromotionCodeRewardsMembersOnly = 'COP-1143',
  CrewTippingUnavailable = 'COP-1145',
  PromotionDeliveryOnly = 'COP-1146',
  PromotionPickUpOnly = 'COP-1147',
  UnableToCancelDeliveryWithProvider = 'COP-1148',
  MaxEntreeLimitExceeded = 'COP-1150'
}

export interface PromoErrorParams {
  header: string;
  content: string;
  firstButton: string;
  secondButton: string;
  firstOnClick(): Promise<void>;
  secondOnClick?(): Promise<void>;
  onClose(): Promise<void>;
  errorType: PromoOnlyErrorTypes;
}

export type FACPromoOptions = { mode: 'pickup', pickupSearchInput?: string; } | { mode: 'delivery', deliverySearchInput?: string; };

export enum PromoOnlyErrorTypes {
  DELIVERY_ONLY = 'DELIVERY_ONLY',
  PICKUP_ONLY = 'PICKUP_ONLY'
}

export interface OrderWithContext {
  etag: string;

  /** These discounts/promotions will be the full details returned from the Promotions API.
   *  Order.discounts has pared-down fields and are returned directly on the Order object from Order API.
   */
  discounts: Promotion[] | null;
  order: Order;
}

export interface Order {
  bagPickupLocationId: BagPickupLocation;
  orderId: string;
  orderShortCode: string;
  groupOrderId: string;
  restaurantId: number;
  orderDateTimeLocal?: string;
  pickupDateTimeLocal?: string;
  createdDateTimeUtc?: string;
  isAboveStorePayment: boolean;
  isOrderComplete: boolean;
  orderType: OrderType;
  orderStatus: OrderStatus;
  orderDonationAmount: number;
  orderMealsUnitPrice: number;
  orderMealsExtendedPrice: number; // Cost of food without tax and fees

  /** orderMealsExtendedPrice - discounts */
  orderMealsSubTotalAmount: number;

  orderFeeAmount: number; // Cost of all fees
  orderPreDiscountSubTotalAmount: number; // Cost of food and fees
  orderDiscountAmount: number;
  orderSubTotalAmount: number; // Cost of food and fees with discounts applied
  orderTaxAmount: number; // Tax
  orderTipAmount: number; // Tip
  orderCrewTipAmount: number; // Crew Tip
  orderTotalAmount: number; // Final cost
  taxWasCalculated: boolean;
  discountsWereCalculated: boolean;
  discountErrorCode: string | null;
  discountErrorContent: string | null;
  deliveryFeeObtained: boolean;
  isOrderActuallyEligibleForDelivery: boolean;
  isOrderPotentiallyEligibleForDelivery: boolean;
  isOrderAvailable: boolean;
  meals: OrderMeal[] | null;
  nonFoodItems: NonFoodItem[] | null;

  discounts:
  | {
    discountCode: string;
    discountName: string;
    discountAmount: number;
    discountIsReusable: boolean;
  }[]
  | null;

  donations:
  | {
    charitableCampaignId: string;
    donationAmount: string;
    donationType: string;
  }[]
  | null;

  delivery: {
    deliveryId: string;
    dropOffAddressLine1: string;
    dropOffAddressLine2: string;
    dropOffAddressCity: string;
    dropOffAddressState: string;
    dropOffAddressZipCode: string;
    dropOffAddressCountryCode: string;
    dropOffInstructions: string;
    dropOffAddressId: string;
    dropOffContactFirstName: string;
    dropOffContactLastName: string;
    dropOffContactPhoneNumber: string;
    feeAmount: number;
    feeDiscountAmount: number;
    feeSubTotalAmount: number;
    feeTaxAmount: number;
    tipAmount: number;
    totalAmount: number;
    meetsMinimumOrderValue: boolean;
    minimumOrderValue: number;
  } | null;
  groupOrderInvitees: any[] | null;

  /** groupOrderMessage is customer input data and should be treated as unsafe */
  groupOrderMessage: string;
  customerFirstName: string;
  taxesAndFees: {
    deliveryServiceFee: number;
    tax: number;
    taxAndFeeTotal: number;
  };

  payments: {
    amount: number;
    lastFourAccountNumbers: string;
    paymentTypeName: string;
  }[],
}

export interface BasicOrder {
  meals: BasicOrderMeal[];
}

export interface BasicOrderMeal {
  mealName: string;
  entrees: BasicOrderMealItemWithContents[] | null;
  sides: BasicOrderMealItem[] | null;
  drinks: BasicOrderMealItem[] | null;
}

export interface OrderMeal extends BasicOrderMeal {
  mealId: string;
  preconfiguredMealId?: string;
  preconfiguredMealName?: string;
  mealUnitPrice: number;
  mealExtendedPrice: number;
  mealDiscountAmount: number;
  mealSubTotalAmount: number;
  mealTaxAmount: number;
  mealTotalAmount: number;
  isMealAvailable: boolean;
  isHostMeal: boolean;
  wasMealModified: boolean;
  entrees: OrderMealEntree[] | null;
  sides: OrderMealItem[] | null;
  drinks: OrderMealItem[] | null;
}

export interface BasicOrderMealItem {
  menuItemId: string;
  menuItemName: string;
  quantity: number;
  isUpSell?: boolean;
  customizationId?: number;
}

export interface NonFoodItem {
  nonFoodItemId: string;
  menuItemId: string;
  menuItemName: string;
  menuItemType: string;
  isUpSell?: boolean;
  itemUnitPrice: number;
  quantity: number;
  pointOfSaleId: number;
}

export interface OrderMealItem extends BasicOrderMealItem {
  itemUnitPrice: number;
  itemExtendedPrice: number;
  itemDiscountAmount: number;
  itemSubTotalAmount: number;
  itemTaxAmount: number;
  itemTotalAmount: number;
  isItemAvailable: boolean;
  preconfiguredItemName?: string;
}

export interface BasicOrderMealItemWithContents extends BasicOrderMealItem {
  contents: BasicOrderMealItem[] | null;
}

export interface OrderMealEntree extends BasicOrderMealItemWithContents {
  entreeId: string;
  entreeUnitPrice: number;
  entreeExtendedPrice: number;
  entreeDiscountAmount: number;
  entreeSubTotalAmount: number;
  entreeTaxAmount: number;
  entreeTotalAmount: number;
  isEntreeAvailable: boolean;
  preconfiguredItemName: string | null;
  contents: OrderMealEntreeContents[] | null;
}

export interface OrderMealEntreeContents extends BasicOrderMealItem {
  entreeContentId: string;
  menuItemName: string;
  customizationId: number;
  itemUnitPrice: number;
  itemExtendedPrice: number;
  itemDiscountAmount: number;
  itemSubTotalAmount: number;
  itemTaxAmount: number;
  itemTotalAmount: number;
  isItemAvailable: boolean;
}

export interface OrderMealRequestEntree extends BasicOrderMealItem {
  contents: BasicOrderMealItem[];
}

export interface OrderMealRequest {
  meal: OrderMealRequestMeal;
}

export interface OrderMealRequestMeal {
  mealName: string;
  entrees: OrderMealRequestEntree[] | null;
  sides: BasicOrderMealItem[] | null;
  drinks: BasicOrderMealItem[] | null;
}

export interface SubmitOrderRequest {
  pickupDateTime: string;
  isAboveStorePayment: boolean;
  payments: SubmitOrderRequestPaymentMethod[];
  estimateId?: string;
  personalization?: { [id: string]: string };
}

export interface SubmitOrderRequestPaymentMethod {
  paymentType: SubmitOrderRequestPaymentMethodType;
  chipotleWalletId?: number;
  cardHolderName: string;
  giftCardNumber?: string;
  creditCardSingleUseToken: string;
  creditCardExpiration?: string;
  creditCardType?: string;
  creditCardZipCode?: string;
  ephemeralPublicKey?: string;
  ephemeralPublicKeyHash?: string;
  merchantIdentifier?: string;
  paySignature?: string;
  nativeWalletData?: string;
  transactionId?: string;
  version?: string;
  amount?: number;
  tag?: string;
  paymentProviderId?: string;
  lastFourAccountNumbers?: string;
  bagPickupLocation?: BagPickupLocation;
}

export interface NonFoodItemRequest {
  menuItemId: string;
  quantity: number;
  isUpSell: boolean;
}

export enum SubmitOrderRequestPaymentMethodType {
  ChipotleWalletGiftCard = 1,
  ChipotleWalletCreditCard = 2,
  GiftCard = 3,
  CreditCard = 4,
  ApplePay = 5,
  AndroidPay = 6,
  GooglePay = 7,
}

export interface UpdateDeliveryInfoOnOrderRequest {
  dropOffAddressId?: string;
  dropOffAddressLine1: string;
  dropOffAddressLine2?: string;
  dropOffAddressCity: string;
  dropOffAddressState: string;
  dropOffAddressZipCode: string;
  dropOffAddressCountryCode: string;
  dropOffInstructions?: string;
  dropOffContactFirstName?: string;
  dropOffContactLastName?: string;
  dropOffContactPhoneNumber?: string;
}

export interface OrderApiError {
  errorCode: OrderErrorCode;
  errorData: string;
  errorDescription: string;
}

export class OrderApiException extends Error {
  constructor(public errors: OrderApiError[]) {
    super();
  }
}

export enum BagPickupLocation {
  Shelf = 1,
  Chipotlane = 2,
  LowerShelf = 3,
  HoldForPayment = 4,
  WalkUpWindow = 6,
}

export class OrderNotFoundException extends Error { }

export interface GetDeliveryEstimateRequest {
  dropOffAddress: {
    addressLine1: string;
    addressLine2: string;
    locality: string;
    administrativeArea: string;
    postalCode: string;
    countryCode: string;
  };

  orderValue: number;
  entreeCount: number;
  restaurantId: number;
  currencyCode?: string;
}

export interface DeliveryEstimate {
  restaurantId: number;
  minDeliveryTime: number;
  maxDeliveryTime: number;
  pickupTime: string;
  deliveryFee: number;
  meetsMinimumOrderValue: boolean;
  estimateId: string;
}